import React, { useRef, useState, useEffect } from 'react';
import { PixelGlyph } from './PixelGlyph';

interface MagneticButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'emerald' | 'solar' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showArrow?: boolean;
  target?: string;
  rel?: string;
}

export const MagneticButton: React.FC<MagneticButtonProps> = ({
  children,
  onClick,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  showArrow = true,
  target,
  rel,
}) => {
  const btnRef = useRef<HTMLAnchorElement & HTMLButtonElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isFinePointer, setIsFinePointer] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: fine)');
    setIsFinePointer(mq.matches);

    const handleMqChange = (e: MediaQueryListEvent) => setIsFinePointer(e.matches);
    mq.addEventListener('change', handleMqChange);

    const handleScroll = () => {
      setPosition((prev) => (prev.x === 0 && prev.y === 0 ? prev : { x: 0, y: 0 }));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      mq.removeEventListener('change', handleMqChange);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isFinePointer || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const pullFactor = 0.22;
    const deltaX = (e.clientX - centerX) * pullFactor;
    const deltaY = (e.clientY - centerY) * pullFactor;

    setPosition({ x: deltaX, y: deltaY });
  };

  const resetPosition = () => {
    setPosition({ x: 0, y: 0 });
  };

  const handleClick = () => {
    resetPosition();
    if (onClick) onClick();
  };

  let baseStyles =
    'relative inline-flex items-center justify-between rounded-none font-sans font-medium transition-all duration-300 group overflow-hidden select-none cursor-pointer tracking-wide';
  let sizeStyles =
    size === 'sm'
      ? 'px-4 py-2 text-xs gap-2.5'
      : size === 'lg'
      ? 'px-8 py-4 text-base gap-4 font-semibold'
      : 'px-6 py-3 text-sm gap-3';
  let variantStyles = '';

  switch (variant) {
    case 'primary':
      variantStyles =
        'bg-carbon-900 text-alabaster-100 hover:bg-carbon-950 shadow-sm active:scale-[0.98] border border-carbon-900';
      break;
    case 'emerald':
      variantStyles =
        'bg-chlorophyll text-white hover:bg-chlorophyll-deep shadow-sm active:scale-[0.98] border border-chlorophyll';
      break;
    case 'solar':
      variantStyles =
        'bg-solar-gold text-white hover:bg-solar-amber shadow-sm active:scale-[0.98] border border-solar-gold';
      break;
    case 'secondary':
      variantStyles =
        'bg-white text-carbon-900 border border-carbon-900/15 hover:border-chlorophyll hover:text-chlorophyll-deep shadow-paper active:scale-[0.98]';
      break;
    case 'ghost':
      variantStyles =
        'bg-transparent text-carbon-900/70 hover:text-carbon-900 hover:bg-black/[0.03] active:scale-[0.98]';
      break;
  }

  const iconCircleStyle =
    variant === 'primary'
      ? 'bg-white/10 text-white'
      : variant === 'emerald'
      ? 'bg-white/20 text-white'
      : variant === 'secondary'
      ? 'bg-carbon-900/5 text-carbon-900 group-hover:text-chlorophyll-deep'
      : 'text-current';

  const content = (
    <>
      <span className="relative z-10 transition-transform duration-300 group-hover:translate-x-0.5">
        {children}
      </span>
      {showArrow && (
        <span
          className={`relative z-10 w-6 h-6 flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12 ${iconCircleStyle}`}
        >
          <PixelGlyph name="arrow-up-right" size={12} color="currentColor" />
        </span>
      )}
    </>
  );

  const style =
    isFinePointer && (position.x !== 0 || position.y !== 0)
      ? {
          transform: `translate3d(${position.x}px, ${position.y}px, 0)`,
          transition: 'transform 0.15s ease-out',
        }
      : {
          transform: 'translate3d(0, 0, 0)',
          transition: 'transform 0.45s cubic-bezier(0.16, 1, 0.3, 1)',
        };

  if (href) {
    return (
      <a
        ref={btnRef}
        href={href}
        target={target}
        rel={rel}
        onClick={handleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={resetPosition}
        onTouchEnd={resetPosition}
        onTouchCancel={resetPosition}
        onBlur={resetPosition}
        style={style}
        className={`${baseStyles} ${sizeStyles} ${variantStyles} ${className}`}
        data-cursor="interactive"
      >
        {content}
      </a>
    );
  }

  return (
    <button
      ref={btnRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={resetPosition}
      onTouchEnd={resetPosition}
      onTouchCancel={resetPosition}
      onBlur={resetPosition}
      style={style}
      className={`${baseStyles} ${sizeStyles} ${variantStyles} ${className}`}
      data-cursor="interactive"
    >
      {content}
    </button>
  );
};
